<script>
  import { onMount, tick } from "svelte";
  import { loadMapLibre, prefetchMapLibre } from "./loadMapLibre.js";

  let {
    center = [36.8219, -1.2921],
    zoom = 12,
    filtersSelector = "#vacancy-filters",
    initialResultsHtml = "",
  } = $props();

  // List-first: MapLibre only loads when Map / Split is used.
  let view = $state("list");
  let mapEmpty = $state(false);
  let mapLoading = $state(false);
  let mapError = $state("");
  let resultsEl = $state(null);
  let mapEl = $state(null);

  let maplibregl = null;
  let map = null;
  let popup = null;
  let mapTimer = null;
  let mapReady = false;

  function wantsMap(mode = view) {
    return mode === "map" || mode === "split";
  }

  function formatRent(value) {
    if (value == null) return "—";
    return "KSh " + Math.round(value).toLocaleString();
  }

  function popupHtml(props) {
    const units = props.units || [];
    const rows = units
      .map(
        (u) =>
          `<a href="${u.url}" class="block py-2 border-b border-slate-100 last:border-0 hover:text-[#E85D04]">` +
          `<span class="font-bold text-sm">Unit ${u.unit_number}</span>` +
          `<span class="block text-[10px] uppercase tracking-widest text-slate-400">` +
          `${u.housing_label || (u.bedrooms + " bed")} · ${formatRent(u.asking_rent)}` +
          `</span></a>`
      )
      .join("");
    return (
      `<div class="min-w-[12rem]">` +
      `<div class="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Qeja</div>` +
      `<div class="font-bold text-base mb-2">${props.name || "Property"}</div>` +
      `<div class="text-xs text-slate-500 mb-2">${props.neighbourhood || props.city || ""}` +
      ` · ${props.unit_count} vacant</div>` +
      rows +
      `</div>`
    );
  }

  function geojsonUrl() {
    const form = document.querySelector(filtersSelector);
    if (!form) return "";
    const params = new URLSearchParams(new FormData(form));
    return form.dataset.geojsonUrl + "?" + params.toString();
  }

  async function ensureMap() {
    if (map) return map;
    if (!mapEl) return null;

    mapLoading = true;
    mapError = "";
    try {
      maplibregl = await loadMapLibre();
      if (map) return map;

      map = new maplibregl.Map({
        container: mapEl,
        style: "https://tiles.openfreemap.org/styles/liberty",
        center,
        zoom,
      });
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
      popup = new maplibregl.Popup({ closeButton: true, maxWidth: "280px" });

      await new Promise((resolve) => {
        map.on("load", () => {
          map.addSource("vacancies", {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
          });
          map.addLayer({
            id: "vacancies-circle",
            type: "circle",
            source: "vacancies",
            paint: {
              "circle-radius": ["interpolate", ["linear"], ["get", "unit_count"], 1, 9, 5, 14],
              "circle-color": "#E85D04",
              "circle-stroke-width": 2,
              "circle-stroke-color": "#ffffff",
            },
          });
          map.on("click", "vacancies-circle", (e) => {
            const f = e.features[0];
            const props = Object.assign({}, f.properties);
            if (typeof props.units === "string") {
              try {
                props.units = JSON.parse(props.units);
              } catch {
                props.units = [];
              }
            }
            popup
              .setLngLat(f.geometry.coordinates.slice())
              .setHTML(popupHtml(props))
              .addTo(map);
          });
          map.on("mouseenter", "vacancies-circle", () => {
            map.getCanvas().style.cursor = "pointer";
          });
          map.on("mouseleave", "vacancies-circle", () => {
            map.getCanvas().style.cursor = "";
          });
          mapReady = true;
          resolve();
        });
      });

      await refreshMapData();
      return map;
    } catch (err) {
      mapError = "Map could not load. Try again or use list view.";
      console.error(err);
      return null;
    } finally {
      mapLoading = false;
    }
  }

  async function refreshMapData() {
    if (!map || !mapReady) return;
    try {
      const r = await fetch(geojsonUrl(), { headers: { Accept: "application/json" } });
      const data = await r.json();
      (data.features || []).forEach((f) => {
        if (f.properties && Array.isArray(f.properties.units)) {
          f.properties.units = JSON.stringify(f.properties.units);
        }
      });
      const source = map.getSource("vacancies");
      if (source) source.setData(data);
      const hasFeatures = data.features && data.features.length > 0;
      mapEmpty = !hasFeatures;
      if (hasFeatures) {
        const bounds = new maplibregl.LngLatBounds();
        data.features.forEach((f) => bounds.extend(f.geometry.coordinates));
        map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 600 });
      }
    } catch {
      mapEmpty = true;
    }
  }

  function onFiltersChanged() {
    if (!wantsMap() || !map) return;
    clearTimeout(mapTimer);
    mapTimer = setTimeout(() => refreshMapData(), 200);
  }

  async function setView(mode) {
    view = mode;
    await tick();
    if (wantsMap(mode)) {
      await ensureMap();
      if (map) {
        map.resize();
        await refreshMapData();
      }
    } else if (map) {
      map.resize();
    }
  }

  onMount(() => {
    if (resultsEl && initialResultsHtml) {
      resultsEl.innerHTML = initialResultsHtml;
    }

    const form = document.querySelector(filtersSelector);
    const onHtmx = () => onFiltersChanged();
    const onChange = () => onFiltersChanged();
    if (form) {
      form.addEventListener("htmx:afterRequest", onHtmx);
      form.addEventListener("change", onChange);
    }

    // Warm MapLibre after first paint so Map/Split feels instant.
    const idle =
      window.requestIdleCallback ||
      ((cb) => setTimeout(cb, 2000));
    const idleId = idle(() => prefetchMapLibre(), { timeout: 4000 });

    return () => {
      if (form) {
        form.removeEventListener("htmx:afterRequest", onHtmx);
        form.removeEventListener("change", onChange);
      }
      clearTimeout(mapTimer);
      if (typeof window.cancelIdleCallback === "function" && typeof idleId === "number") {
        window.cancelIdleCallback(idleId);
      }
      if (map) {
        map.remove();
        map = null;
      }
      mapReady = false;
    };
  });
</script>

<div class="space-y-6">
  <div class="flex justify-end gap-2" role="group" aria-label="View mode">
    <button
      type="button"
      onclick={() => setView("list")}
      class="px-4 py-3 rounded-xl border border-slate-200 text-[10px] font-black uppercase tracking-widest {view === 'list'
        ? 'bg-fds-onyx text-white'
        : 'bg-white text-fds-onyx'}"
    >
      List
    </button>
    <button
      type="button"
      onclick={() => setView("map")}
      onmouseenter={prefetchMapLibre}
      onfocus={prefetchMapLibre}
      class="px-4 py-3 rounded-xl border border-slate-200 text-[10px] font-black uppercase tracking-widest {view === 'map'
        ? 'bg-fds-onyx text-white'
        : 'bg-white text-fds-onyx'}"
    >
      Map
    </button>
    <button
      type="button"
      onclick={() => setView("split")}
      onmouseenter={prefetchMapLibre}
      onfocus={prefetchMapLibre}
      class="hidden lg:inline-flex px-4 py-3 rounded-xl border border-slate-200 text-[10px] font-black uppercase tracking-widest {view === 'split'
        ? 'bg-fds-onyx text-white'
        : 'bg-white text-fds-onyx'}"
    >
      Split
    </button>
  </div>

  <div class="grid grid-cols-1 gap-6 {view === 'split' ? 'lg:grid-cols-2' : ''}">
    <div class={view === "list" || view === "split" ? "" : "hidden"}>
      <div id="vacancy-results" bind:this={resultsEl}></div>
    </div>

    <div class="space-y-3 {view === 'map' || view === 'split' ? '' : 'hidden'}">
      <div class="bg-white rounded-2xl border border-slate-100 overflow-hidden relative">
        <div id="qeja-map" bind:this={mapEl}></div>
        {#if mapLoading}
          <div class="absolute inset-0 flex items-center justify-center bg-white/90 p-8 text-center">
            <p class="text-sm font-bold text-fds-ink">Loading map…</p>
          </div>
        {:else if mapError}
          <div class="absolute inset-0 flex items-center justify-center bg-white/90 p-8 text-center">
            <div>
              <p class="text-base font-fds-black text-fds-onyx tracking-fds-tighter mb-2">Map unavailable</p>
              <p class="text-sm text-slate-500 mb-4">{mapError}</p>
              <button
                type="button"
                class="px-5 py-2.5 bg-fds-onyx text-white rounded-xl text-[10px] font-black uppercase tracking-widest"
                onclick={() => ensureMap()}
              >
                Retry
              </button>
            </div>
          </div>
        {:else if mapEmpty}
          <div class="absolute inset-0 flex items-center justify-center bg-white/90 p-8 text-center">
            <div>
              <p class="text-lg font-fds-black text-fds-onyx tracking-fds-tighter mb-2">No mapped vacancies</p>
              <p class="text-sm text-slate-500">
                Listings need latitude/longitude on their location to appear here.
              </p>
            </div>
          </div>
        {/if}
      </div>
      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
        Pins are buildings · click for vacant units
      </p>
    </div>
  </div>
</div>

<style>
  #qeja-map {
    height: 28rem;
    width: 100%;
  }
  @media (min-width: 1024px) {
    #qeja-map {
      height: 36rem;
    }
  }
  :global(.maplibregl-popup-content) {
    border-radius: 1rem;
    padding: 0.75rem 1rem;
    font-family: inherit;
    box-shadow: 0 10px 40px rgba(17, 24, 39, 0.15);
  }
  :global(.maplibregl-popup-close-button) {
    font-size: 1.25rem;
    padding: 0.25rem 0.5rem;
  }
</style>
